import { BalanceData, GearItem, PaidReward, RARITY_IDS, Rarity, primaryValue } from "@highmarch/domain";
import { RARITY_LABEL, fmtStat, gearIconFallback, pct } from "./gearUi";
import { btnHtml, fmtCompact, pillHtml } from "./kit";
import { gearIconUrl, resourceIconUrl, uiUrl } from "./portraits";
import { ChestStage } from "../render/chestStage";
import { lib } from "../render/views/common";
import { DEBUG } from "../boot/debug";
import { parkLootFlight } from "./loot";
import { bump } from "./kit";
import { chestSlot } from "./chestSlot";
import { playParkedUnlockCards } from "./unlockCards";
import { essenceIconUrl, sigilIconUrl, sigilWords } from "./forgeArt";
import { cardFocus } from "./chestCards";
import { showItemFocus } from "./deals/pickSheet";

/**
 * What a chest held. `essence` (gear v2): the Forge Essence it paid — a level's own battle chest (domain
 * `chestEssence`) or a reward's; `sigils`: the Fusion Sigils a reward paid with it.
 */
export interface ChestContents {
  gear: GearItem[]; speedUpMinutes: number; gold: number;
  essence?: number;
  sigils?: { readonly rarity: Rarity; readonly n: number } | null;
}

/** A paid reward's chest and forge goods as the reveal's contents (event nodes, achievements, milestones). */
export function paidContents(paid: PaidReward, gear: readonly GearItem[]): ChestContents {
  const chest = paid.chest;
  return {
    gear: [...gear], speedUpMinutes: chest?.speedUpMinutes ?? 0, gold: chest?.gold ?? 0, essence: paid.essence,
    sigils: paid.sigil !== null && paid.sigils > 0 ? { rarity: paid.sigil, n: paid.sigils } : null,
  };
}

/**
 * Chest reveal (art/interface/README.md drop 2 §5, mockup 10): the rarity glow disc pulses behind the chest, the lid
 * swings open (rotateX −70°), then one item card per piece of gear slides up out of the body 0.12 s apart, followed by a
 * loot card with the gold and speed-up pills. COLLECT (or a tap anywhere) skips to the end state. With a `ChestStage` (the HQ
 * model, art/CHEST_HANDOFF.md) the reveal is the full-screen slot machine in chestSlot.ts instead, and this card on
 * the end screen becomes the receipt once it is collected. Presentation only — the chest is banked by the caller
 * before this runs.
 */
export function chestReveal(container: HTMLElement, B: BalanceData, rarity: Rarity, contents: ChestContents, onDone: () => void, stage: ChestStage | null = null): void {
  const rid = RARITY_IDS[rarity];
  if (stage) {
    // The HQ chest gets the full-screen slot machine (chestSlot.ts); the card on the end screen becomes a receipt.
    const cards = [...contents.gear.map((g) => itemCard(B, g)), lootCard(contents)];
    chestSlot({
      host: container.closest<HTMLElement>(".end") ?? document.body, rarity, stage, cards,
      payout: { gold: contents.gold, essence: contents.essence },
      onCollect: () => {
        parkLootFlight({ gold: contents.gold });
        container.classList.add("revealing", "collected");
        container.innerHTML = `<div class="reveal ${rid} receipt"><b class="label disp">${RARITY_LABEL[rarity].toUpperCase()} CHEST</b></div>`;
        // the receipt keeps every card in one row — the gear by name, then the loot — and a gear card opens its focus
        const row = document.createElement("div");
        row.className = "rcards";
        row.append(...contents.gear.map((g) => itemCard(B, g)), lootCard(contents));
        container.querySelector(".reveal")?.append(row);
        row.onclick = (e) => { const c = (e.target as HTMLElement).closest<HTMLElement>(".itemcard"); const f = c ? cardFocus.get(c) : undefined; if (f) f(); };
        container.querySelectorAll(".itemcard.loot .pill").forEach((p, i) => window.setTimeout(() => bump(p, 1.2, 220), i * 70));
        playParkedUnlockCards(unlockHost(container));
        onDone();
      },
    });
    return;
  }
  container.innerHTML = `<div class="reveal ${rid}">
      <div class="stage">
        <img class="glow" decoding="sync" src="${uiUrl(`chests/glow_${rid}.png`)}" alt="">
        <img class="body" decoding="sync" src="${uiUrl(`chests/chest_${rid}_body.png`)}" alt=""><img class="lid" decoding="sync" src="${uiUrl(`chests/chest_${rid}_lid.png`)}" alt="">
      </div>
      <div class="label disp">${RARITY_LABEL[rarity].toUpperCase()} CHEST</div>
      <div class="cards"></div>
      ${btnHtml("COLLECT", "gold", { className: "collect disp hidden" })}
      <small class="hint">tap to skip</small>
    </div>`;
  container.classList.add("revealing");
  const reveal = container.querySelector<HTMLElement>(".reveal")!;
  const cards = reveal.querySelector<HTMLElement>(".cards")!;
  const collect = reveal.querySelector<HTMLButtonElement>(".collect")!;
  const all: HTMLElement[] = contents.gear.map((g) => itemCard(B, g));
  all.push(lootCard(contents));
  for (const c of all) cards.append(c);
  const timers: number[] = [];
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    for (const t of timers) window.clearTimeout(t);
    reveal.classList.add("open", "done");
    for (const c of all) c.classList.add("in");
    collect.classList.remove("hidden");
    reveal.onclick = null;
    collect.onclick = () => {
      reveal.classList.add("collected");
      // The loot flies into the hub pills on the next hub render (loot.ts); here the loot card's pills pop as a receipt.
      parkLootFlight({ gold: contents.gold });
      reveal.querySelectorAll(".itemcard.loot .pill").forEach((p, i) => window.setTimeout(() => bump(p, 1.2, 220), i * 70));
      playParkedUnlockCards(unlockHost(container));
      onDone();
    };
  };
  const dealCards = (after: number) => {
    all.forEach((c, i) => timers.push(window.setTimeout(() => c.classList.add("in"), after + i * 120)));
    timers.push(window.setTimeout(finish, after + all.length * 120 + 250));
  };
  reveal.onclick = () => finish();
  timers.push(window.setTimeout(() => reveal.classList.add("open"), 200));
  dealCards(650);
}

/** How long a reward chest waits for its 3D model before it opens the painted way instead. */
const MODEL_WAIT_MS = 4000;

/**
 * A reward chest away from the level end — an achievement, an event or Endless node, a star chest, a milestone, a
 * crate (M30 #10, Kevin: "the achievements chest looks and animates differently from the end-of-level chest"): the
 * same 3D chest and the same full-screen slot machine (chestSlot.ts: TAP to rattle, the burst, the cards flying out
 * and flipping, COLLECT) as the battle chest, over everything on `parent`, with `title` (what it is for) under the
 * ribbon. The model loads on first use; while it does a dim layer holds the screen, and without it (no WebGL library,
 * a slow load) the painted reveal plays as before. `onDone` after COLLECT. Presentation only: the caller banked it.
 */
export function rewardChest(parent: HTMLElement, B: BalanceData, rarity: Rarity, contents: ChestContents, title: string, onDone: () => void): void {
  const rid = RARITY_IDS[rarity];
  const host = document.createElement("div");
  host.className = "achchest chesthost";
  parent.append(host);
  let started = false;
  const finish = () => { host.remove(); onDone(); };
  const go = () => {
    if (started || !host.isConnected) return;
    started = true;
    if (ChestStage.available(rid)) {
      host.classList.add("slot");
      const stage = new ChestStage(rid, 300, 200);
      chestSlot({
        host, rarity, stage, cards: [...contents.gear.map((g) => itemCard(B, g)), lootCard(contents)],
        payout: { gold: contents.gold, essence: contents.essence },
        onCollect: () => { parkLootFlight({ gold: contents.gold }); finish(); },
      });
      if (title) host.insertAdjacentHTML("beforeend", `<b class="ch-for disp">${title}</b>`);
      return;
    }
    host.innerHTML = `<div class="panel box"><b class="disp">${title}</b><div class="well"></div></div>`;
    chestReveal(host.querySelector<HTMLElement>(".well")!, B, rarity, contents, finish);
  };
  const L = lib();
  if (ChestStage.available(rid) || !L) { go(); return; }
  void L.ensure(ChestStage.modelsFor(rid)).then(go, go);
  window.setTimeout(go, MODEL_WAIT_MS);
}

/** Unlock cards play over the whole UI layer the end screen sits in, not inside the chest block. */
function unlockHost(container: HTMLElement): HTMLElement {
  return container.closest<HTMLElement>(".end")?.parentElement ?? document.body;
}

/** 110×150 parchment item card with the painted gear icon in the well, the name on the first line, the stat on the second. */
function itemCard(B: BalanceData, it: GearItem): HTMLElement {
  const rid = RARITY_IDS[it.rarity];
  const slot = B.gear.slots[it.slot];
  const el = document.createElement("div");
  el.className = `itemcard ${rid}`;
  el.innerHTML = `<div class="rib">${RARITY_LABEL[it.rarity].toUpperCase()}</div><img decoding="sync" src="${gearIconUrl(slot.id, rid)}" alt=""><b>${slot.name} Lv ${it.level}</b><small>${pct(primaryValue(B, it))} ${fmtStat(it.primaryStat)}</small>`;
  gearIconFallback(el.querySelector<HTMLImageElement>(":scope > img"), slot.id, 56);
  el.dataset.focus = "1";
  cardFocus.set(el, () => { void showItemFocus(document.getElementById("ui") ?? document.body, B, it.level, { kind: "gear", rarity: it.rarity, slot: "pick", count: 1 }, {}, it.slot); });
  return el;
}

/** The loot card: gold, the speed-up and (gear v2) the Forge Essence and sigils as pills. */
function lootCard(c: ChestContents): HTMLElement {
  const el = document.createElement("div");
  el.className = "itemcard loot common";
  const speed = c.speedUpMinutes >= 60 ? `${c.speedUpMinutes / 60}h` : `${c.speedUpMinutes}m`;
  const pills = [pillHtml(resourceIconUrl("gold"), fmtCompact(c.gold), "gold")];
  if (c.speedUpMinutes > 0 || !c.essence) pills.push(pillHtml(resourceIconUrl("speedup"), speed));
  if (c.essence && c.essence > 0) pills.push(pillHtml(essenceIconUrl(), `+${fmtCompact(c.essence)}`, "gold", false, "ess"));
  if (c.sigils && c.sigils.n > 0) pills.push(pillHtml(sigilIconUrl(c.sigils.rarity), `+${c.sigils.n}`, "", false, "sig"));
  el.innerHTML = `<div class="rib">LOOT</div><div class="pills">${pills.join("")}</div>`;
  if (c.essence && c.essence > 0) el.setAttribute("aria-label", `Loot: ${fmtCompact(c.gold)} gold, ${c.essence} Forge Essence${c.sigils && c.sigils.n > 0 ? `, ${sigilWords(c.sigils.rarity, c.sigils.n)}` : ""}`);
  return el;
}

// ?debug: the capture scripts open a reward chest without a battle (`__tdChest.reward(rarity)`: the player's newest
// gear as its contents) to check the reveal's look headless.
if (DEBUG && typeof window !== "undefined") {
  (window as unknown as { __tdChest: unknown }).__tdChest = {
    rewardChest, chestReveal,
    reward: (rarity: Rarity, B: BalanceData, gear: GearItem[], parent: HTMLElement = document.getElementById("ui") ?? document.body) =>
      rewardChest(parent, B, rarity, { gear, speedUpMinutes: 60, gold: 1250, essence: 40 }, "Debug chest", () => undefined),
  };
}
